import type { ThemeDefinition, ThemeInstance } from 'vuetify'
import type { Theming } from './types'
import { ThemeMode } from './types'

export const curTheme = ref()

export function overwriteThemes(themes: Theming, theme: ThemeInstance, curTheme: Ref<string>): void {
  const light = theme.themes.value.light
  const dark = theme.themes.value.dark

  theme.themes.value = {
    light,
    dark,
    lightZitadel: lightTheme(light, themes) as any,
    darkZitadel: darkTheme(dark, themes) as any,
  }

  const darkThemeString = theme.themes.value.lightZitadel !== undefined ? 'darkZitadel' : 'dark'
  const lightThemeString = theme.themes.value.darkZitadel !== undefined ? 'lightZitadel' : 'light'
  const isDark = useDark()
  const icon = useFavicon()

  const iconDefault = icon.value

  // update curTheme
  watchEffect(() => {
    if ([ThemeMode.auto, ThemeMode.unspecified].includes(themes.themeMode))
      curTheme.value = isDark.value ? darkThemeString : lightThemeString
    else if (ThemeMode.dark === themes.themeMode)
      curTheme.value = darkThemeString
    else
      curTheme.value = lightThemeString
  })

  // update favicon when curTheme changes
  watchEffect(() => {
    const curIcon = theme.themes.value[curTheme.value].variables.icon
    if (typeof curIcon === 'string' && curIcon.length > 0)
      icon.value = curIcon
    else icon.value = iconDefault
  })
}

function darken(color: string, amount: number): string {
  return `#${color
    .replace(/^#/, '')
    .replace(/../g, color => (`0${Math.min(255, Math.max(0, Number.parseInt(color, 16) - amount)).toString(16)}`).slice(-2))}`
}

function lighten(color: string, amount: number): string {
  return darken(color, -amount)
}

export function darkTheme(base: ThemeDefinition, theme: Theming): ThemeDefinition | undefined {
  return [ThemeMode.light, ThemeMode.auto].includes(theme.themeMode)
    ? {
        colors: Object.assign({}, base.colors, {
          background: theme.backgroundColorDark,
          background2: lighten(theme.backgroundColorDark, 10),
          background3: darken(theme.backgroundColorDark, 10),
          surface: theme.backgroundColorDark,
          primary: theme.primaryColorDark,
          primary2: lighten(theme.primaryColorDark, 20),
          primary3: darken(theme.primaryColorDark, 20),
          secondary: theme.fontColorDark,
          error: '#FF0000',
          success: '#00FF00',
          warning: '#FFA500',
          info: '#FFA500',
        }),
        variables: Object.assign({}, base.variables, {
          icon: theme.iconUrlDark,
          logo: theme.logoUrlDark,
        }),
        dark: true,
      }
    : undefined
}

export function lightTheme(base: ThemeDefinition, theme: Theming): ThemeDefinition | undefined {
  return [ThemeMode.light, ThemeMode.auto].includes(theme.themeMode)
    ? {
        colors: Object.assign({}, base.colors, {
          background: theme.backgroundColor,
          background2: darken(theme.backgroundColor, 10),
          background3: lighten(theme.backgroundColor, 10),
          surface: theme.backgroundColor,
          primary: theme.primaryColor,
          primary2: darken(theme.primaryColor, 20),
          primary3: lighten(theme.primaryColor, 20),
          secondary: theme.fontColor,
          error: '#FF0000',
          success: '#6AA84F',
          warning: '#FFA500',
          info: '#FFA500',
        }),
        variables: Object.assign({}, base.variables, {
          icon: theme.iconUrlDark,
          logo: theme.logoUrlDark,
        }),
        dark: false,
      }
    : undefined
}
